import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import SectionHead from '../SectionHead'
import NewsCover from '../NewsCover'

import eventEmitter, { types } from '../../shared/eventEmitter'
import { optionsDateWhioutTime } from '../../shared/const'
import { newsLocation } from '../../shared/helpers'

import styles from './style.module.scss'
import { formatDate } from '../../shared/datetime'

/**
 * Veranstaltungen und Neuigkeiten als Kachelreihe.
 *
 * Auf dem Dashboard der Lehrkraft standen sie bisher als schmale Spalte
 * zwischen zwei weiteren Listen — drei gleich aussehende Blöcke nebeneinander,
 * von denen nur einer etwas Neues enthält. Hier bekommen sie die volle Breite,
 * die Art (Veranstaltung oder Neuigkeit) steht an der Kachel, und die ganze
 * Kachel öffnet den Eintrag.
 *
 * Das Datum steht in österreichischer Schreibweise. `date-fns` formatierte hier
 * mit der Standardsprache Englisch ("03 Mar 2026") — mitten in einer deutschen
 * Oberfläche.
 */

const EXCERPT_LENGTH = 150

const fmt = value => formatDate(value, optionsDateWhioutTime) || null
/*
 * Zwei Zeitpunkte als Spanne — aber nur, wenn es wirklich zwei sind. Eine
 * Veranstaltung an einem Tag stand sonst als „12 Nov 2026 – 12 Nov 2026" da.
 */
const spanne = (von, bis) => (von && bis && von !== bis ? `${von} – ${bis}` : von || bis || '')

export default function NewsHighlights({ items, toFullList, className, single }) {
	const { t } = useTranslation()

	const openItem = item =>
		eventEmitter.emit(types.openDetailFeedModal, {
			id: item.id,
			title: item.title,
			description: item.description,
			externalLink: item.externalLink,
			place: item.place,
			street: item.street,
			zip: item.zip,
			city: item.city,
			country: item.country,
			startDate: item.startDate,
			endDate: item.endDate,
			eventStart: item.eventStart,
			eventEnd: item.eventEnd,
			states: item.states,
			roles: item.roles,
			schoolType: item.schoolType,
			companyTypes: item.companyTypes,
			companyBranch: item.companyBranch,
			newsType: item.newsType,
			imageHashName: item.imageHashName
		})

	return (
		<section className={clsx(styles.root, className)}>
			{/* Dieselbe Überschrift wie in jedem anderen Abschnitt — siehe UI/SectionHead. */}
			<SectionHead title={t('Events and news')} linkTo={toFullList} linkLabel={t('Go To News')} />

			{items?.length ? (
				<div className={clsx(styles.grid, single && styles.gridSingle)}>
					{items.map(item => {
						// Der Termin der Veranstaltung (eventStart/eventEnd), nicht die
						// Anzeigedauer der Redaktion (startDate/endDate) — an der Kachel
						// gelesen wirkt die zweite wie ein Termin, den es nicht gibt.
						const termin = spanne(fmt(item.eventStart), fmt(item.eventEnd))
						const ort = newsLocation(item)
						const excerpt =
							item.description?.length > EXCERPT_LENGTH
								? `${item.description.substring(0, EXCERPT_LENGTH).trimEnd()}…`
								: item.description

						return (
							<article
								key={item.id}
								className={styles.card}
								role="button"
								tabIndex={0}
								title={t('View Detail')}
								onClick={() => openItem(item)}
								onKeyDown={event => {
									if (event.key !== 'Enter' && event.key !== ' ') return
									event.preventDefault()
									openItem(item)
								}}
							>
								{/* Das Bild zuerst: Auf dem Dashboard steht die Kachel
								    zwischen zwei Textblöcken — es ist das Einzige,
								    was sie ohne Lesen unterscheidbar macht. */}
								<NewsCover className={styles.cover} hashName={item.imageHashName} alt={item.title} />

								{item.newsType && (
									<span
										className={clsx(
											styles.badge,
											item.newsType === 'EVENT' ? styles.badgeEvent : styles.badgeNews
										)}
									>
										{t(item.newsType)}
									</span>
								)}
								<h3 className={styles.cardTitle}>{item.title}</h3>
								<p className={styles.excerpt}>{excerpt}</p>
								<footer className={styles.meta}>
									{!!termin && <span className={styles.date}>{termin}</span>}
									{!!ort && <span className={styles.place}>{ort}</span>}
								</footer>
							</article>
						)
					})}
				</div>
			) : (
				<p className={styles.empty}>{t('No news yet')}</p>
			)}
		</section>
	)
}

NewsHighlights.propTypes = {
	items: PropTypes.array,
	toFullList: PropTypes.string.isRequired,
	className: PropTypes.string,
	// Als Spalte im Dashboard-Raster: die Kacheln untereinander statt nebeneinander.
	single: PropTypes.bool
}
